import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Filter,
  FileText,
  DollarSign,
  Users,
  TrendingUp,
  Diamond,
  Gem
} from 'lucide-react';
import { multinivelService } from '@/services/multinivel.service';
import { GetReceiptsDto, ReceiptResponseDto, PaginatedReceiptsResponseDto, ReceiptStatistics } from '@/types/multinivel';
import { useToast } from '@/hooks/use-toast';
import LoadingSpinner from '@/components/LoadingSpinner';

const RecibosMultinivel: React.FC = () => {
  const { toast } = useToast();
  const [receipts, setReceipts] = useState<ReceiptResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState<ReceiptStatistics | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Estados dos filtros
  const [filters, setFilters] = useState<GetReceiptsDto>({
    page: 1,
    limit: 20
  });

  // Estados dos formulários de filtro
  const [formFilters, setFormFilters] = useState({
    nome_beneficiaria: '',
    nome_vendedora: '',
    tipo: '',
    mes_referencia: '',
    valor_venda_min: '',
    valor_venda_max: '',
    nome_cliente: '',
    code_pagarme: ''
  });

  useEffect(() => {
    fetchReceipts();
  }, [filters]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const response: PaginatedReceiptsResponseDto = await multinivelService.getReceipts(filters);
      setReceipts(response.data);
      setStatistics(response.estatisticas);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar recibos. Tente novamente.",
        variant: "destructive",
      });
      console.error('Erro ao buscar recibos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFormFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const applyFilters = () => {
    const newFilters: GetReceiptsDto = {
      page: 1,
      limit: filters.limit
    };

    // Aplica apenas filtros com valores
    if (formFilters.nome_beneficiaria.trim() !== '') {
      newFilters.nome_beneficiaria = formFilters.nome_beneficiaria;
    }
    if (formFilters.nome_vendedora.trim() !== '') {
      newFilters.nome_vendedora = formFilters.nome_vendedora;
    }
    if (formFilters.tipo.trim() !== '') {
      newFilters.tipo = formFilters.tipo;
    }
    if (formFilters.mes_referencia.trim() !== '') {
      newFilters.mes_referencia = formFilters.mes_referencia;
    }
    if (formFilters.valor_venda_min.trim() !== '') {
      newFilters.valor_venda_min = parseFloat(formFilters.valor_venda_min);
    }
    if (formFilters.valor_venda_max.trim() !== '') {
      newFilters.valor_venda_max = parseFloat(formFilters.valor_venda_max);
    }
    if (formFilters.nome_cliente.trim() !== '') {
      newFilters.nome_cliente = formFilters.nome_cliente;
    }
    if (formFilters.code_pagarme.trim() !== '') {
      newFilters.code_pagarme = formFilters.code_pagarme;
    }

    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFormFilters({
      nome_beneficiaria: '',
      nome_vendedora: '',
      tipo: '',
      mes_referencia: '',
      valor_venda_min: '',
      valor_venda_max: '',
      nome_cliente: '',
      code_pagarme: ''
    });
    setFilters({
      page: 1,
      limit: filters.limit
    });
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getTipoBadgeColor = (tipo: string) => {
    switch (tipo) {
      case 'venda':
        return 'bg-green-100 text-green-800';
      case 'aprovacao':
        return 'bg-blue-100 text-blue-800';
      case 'primeira_venda':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Recibos Multinível
          </h1>
          <p className="text-gray-600 mt-2">
            Gerencie e visualize todos os recibos do sistema multinível
          </p>
        </div>
        <Badge variant="outline" className="text-primary border-primary">
          Admin
        </Badge>
      </div>

      <Separator />

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Recibos
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics?.quantidade_total_recibos || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Carregando...' : 'Recibos encontrados'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Valor Total de Vendas
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics ? formatCurrency(statistics.valor_venda_total) : 'R$ 0,00'}
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Carregando...' : 'Valor total movimentado'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Diamantes Gerados
            </CardTitle>
            <Gem className="h-4 w-4 text-gold" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gold">
              {statistics?.diamantes_totais_gerados?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Carregando...' : 
              (
                <>
                  1ª Venda: <span className="text-gold font-bold">{statistics?.diamantes_primeira_venda?.toFixed(2) || '0.00'}</span> 
                </>
              )
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Beneficiárias Únicas
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics?.quantidade_beneficiarias_unicas || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Carregando...' : 'Usuárias que receberam comissões'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Card adicional com vendas únicas e ticket médio */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Vendas Únicas
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics?.quantidade_vendas_unicas || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Carregando...' : 'Transações que geraram recibos'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Ticket Médio
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics && statistics.quantidade_vendas_unicas > 0 
                ? formatCurrency(statistics.valor_venda_total / statistics.quantidade_vendas_unicas)
                : 'R$ 0,00'
              }
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Carregando...' : 'Valor médio por venda'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
          <CardDescription>
            Use os filtros abaixo para refinar sua busca
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nome_beneficiaria">Nome da Beneficiária</Label>
              <Input
                id="nome_beneficiaria"
                placeholder="Digite o nome..."
                value={formFilters.nome_beneficiaria}
                onChange={(e) => handleFilterChange('nome_beneficiaria', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nome_vendedora">Nome da Vendedora</Label>
              <Input
                id="nome_vendedora"
                placeholder="Digite o nome..."
                value={formFilters.nome_vendedora}
                onChange={(e) => handleFilterChange('nome_vendedora', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              {/* <Label htmlFor="tipo">Tipo</Label>
              <Select value={formFilters.tipo} onValueChange={(value) => handleFilterChange('tipo', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos</SelectItem>
                  <SelectItem value="venda">Venda</SelectItem>
                  <SelectItem value="aprovacao">Aprovação</SelectItem>
                  <SelectItem value="primeira_venda">Primeira Venda</SelectItem>
                </SelectContent>
              </Select> */}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mes_referencia">Mês de Referência</Label>
              <Input
                id="mes_referencia"
                type="month"
                value={formFilters.mes_referencia}
                onChange={(e) => handleFilterChange('mes_referencia', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor_venda_min">Valor Mínimo</Label>
              <Input
                id="valor_venda_min"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formFilters.valor_venda_min}
                onChange={(e) => handleFilterChange('valor_venda_min', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor_venda_max">Valor Máximo</Label>
              <Input
                id="valor_venda_max"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formFilters.valor_venda_max}
                onChange={(e) => handleFilterChange('valor_venda_max', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nome_cliente">Nome do Cliente</Label>
              <Input
                id="nome_cliente"
                placeholder="Digite o nome..."
                value={formFilters.nome_cliente}
                onChange={(e) => handleFilterChange('nome_cliente', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code_pagarme">Código Pagar.me</Label>
              <Input
                id="code_pagarme"
                placeholder="Digite o código..."
                value={formFilters.code_pagarme}
                onChange={(e) => handleFilterChange('code_pagarme', e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <Button onClick={applyFilters} className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Aplicar Filtros
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Recibos</CardTitle>
          <CardDescription>
            {pagination.total} recibos encontrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingSpinner isLoading={true} title="Carregando recibos..." />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Nível</TableHead>
                      <TableHead>Beneficiária</TableHead>
                      <TableHead>Indicada</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Venda</TableHead>
                      <TableHead>Percentual</TableHead>
                      <TableHead>Diamantes</TableHead>
                      <TableHead>Mês Ref.</TableHead>
                      <TableHead>Processado</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receipts.map((receipt) => (
                      <TableRow key={receipt.id}>
                        <TableCell className="font-medium">{receipt.id}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">Nível {receipt.nivel}</Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{receipt.beneficiaria.nome_razao_social}</div>
                            <div className="text-sm text-gray-500">#{receipt.beneficiaria.codigo} • [{receipt.beneficiaria.role == 'cliente' ? 'cliente' : receipt.beneficiaria.role.substring(0,5)}]</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{receipt.vendedora.nome_razao_social}</div>
                            <div className="text-sm text-gray-500">#{receipt.vendedora.codigo} • [{receipt.vendedora.role == 'cliente' ? 'cliente' : receipt.vendedora.role.substring(0,5)}]</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getTipoBadgeColor(receipt.tipo)}>
                            {receipt.tipo}
                          </Badge>
                        </TableCell>
                        <TableCell>
                           <div className="font-medium">{formatCurrency(receipt.valor_venda)}</div>
                          <div className="text-sm text-gray-500">#{receipt.venda.codepagarme}</div>
                        </TableCell>
                        <TableCell>{receipt.percentual_aplicado}%</TableCell>
                        <TableCell className="text-gold font-medium">
                          {receipt.diamantes_gerados}
                        </TableCell>
                        <TableCell>{receipt.mes_referencia}</TableCell>
                        <TableCell>
                          <Badge variant={receipt.processado ? "default" : "secondary"}>
                            {receipt.processado ? "Sim" : "Não"}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(receipt.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Paginação */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-500">
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} resultados
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <span className="text-sm">
                    Página {pagination.page} de {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RecibosMultinivel;
